import { useEffect } from "react";
import type { ReactNode } from "react";
import { IoClose } from "react-icons/io5";

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  showCloseButton?: boolean;
}

export const Dialog = ({
  isOpen,
  onClose,
  children,
  title,
  showCloseButton = true,
}: DialogProps) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fechar"
          >
            <IoClose className="text-xl text-gray-500" />
          </button>
        )}

        {title && (
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          </div>
        )}

        <div className="px-6 py-4">{children}</div>
      </div>
    </div>
  );
};

interface DialogContentProps {
  children: ReactNode;
}

export const DialogContent = ({ children }: DialogContentProps) => {
  return <div className="text-gray-600 text-sm leading-relaxed">{children}</div>;
};

interface DialogFooterProps {
  children: ReactNode;
}

export const DialogFooter = ({ children }: DialogFooterProps) => {
  return <div className="mt-6 flex flex-col sm:flex-row gap-3">{children}</div>;
};
